import { DotsThreeVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeVertical";
import { useCallback, useRef, useState } from "react";
import cn from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox, ContextMenu } from "@hicya/ui";

import { HabitIcon } from "@/components/ui/habit-icon";
import { CountDisplay } from "@/components/ui/count-display";
import type { HabitDisplay } from "@/hooks/use-daily-view";

import styles from "./habit-row.module.scss";

interface HabitRowProps {
  display: HabitDisplay;
  onComplete: () => void;
  onIncrement: () => void;
  onSkip: () => void;
  onBackfill: () => void;
  onEdit: () => void;
  onViewStats: () => void;
  onAddNote: () => void;
}

export function HabitRow({
  display,
  onComplete,
  onIncrement,
  onSkip,
  onBackfill,
  onEdit,
  onViewStats,
  onAddNote,
}: HabitRowProps) {
  const { habit, dayState, score, isColdStart, completion, effectiveTarget, category } = display;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: habit.id,
  });
  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDone = dayState === "done" || dayState === "skipped";
  const isBoolean = habit.type === "boolean";
  const currentCount = completion?.value ?? 0;

  const handleLongPressStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
    longPressTimer.current = setTimeout(() => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setContextMenu({ x: rect.left, y: rect.bottom + 4 });
    }, 500);
  }, []);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTap = useCallback(() => {
    if (contextMenu) return;
    if (isBoolean) {
      onComplete();
    }
  }, [contextMenu, isBoolean, onComplete]);

  const handleMoreClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setContextMenu({ x: rect.left, y: rect.bottom + 4 });
  }, []);

  // Score label
  const scoreLabel = isColdStart
    ? `D${Math.max(1, Math.round(score * 14))}`
    : `${Math.round(score * 100)}%`;

  return (
    <>
      <div
        ref={setNodeRef}
        style={sortableStyle}
        className={cn(styles.row, isDone && styles.done)}
        onClick={handleTap}
        onMouseDown={handleLongPressStart}
        onMouseUp={handleLongPressEnd}
        onMouseLeave={handleLongPressEnd}
        onTouchStart={handleLongPressStart}
        onTouchEnd={handleLongPressEnd}
        data-testid={`habit-row-${habit.id}`}
        {...attributes}
        {...listeners}
        role="group"
        aria-label={habit.name}
      >
        <span className={styles.icon}>
          <HabitIcon value={habit.icon} />
        </span>
        {category && (
          <span
            className={styles.categoryDot}
            style={{ backgroundColor: category.color }}
            title={category.name}
          />
        )}
        <span className={styles.name}>{habit.name}</span>
        <span className={styles.score}>{scoreLabel}</span>
        <button
          type="button"
          className={styles.moreButton}
          onClick={handleMoreClick}
          aria-label={`Actions for ${habit.name}`}
        >
          <DotsThreeVerticalIcon size={20} aria-hidden="true" />
        </button>
        <div className={styles.action}>
          {isBoolean ? (
            <Checkbox
              checked={isDone}
              onChange={onComplete}
              size={28}
              aria-label={
                isDone ? `Mark ${habit.name} as incomplete` : `Mark ${habit.name} as complete`
              }
              data-testid={`habit-checkbox-${habit.id}`}
            />
          ) : (
            <CountDisplay
              current={currentCount}
              target={effectiveTarget}
              onIncrement={onIncrement}
              aria-label={`${currentCount} of ${effectiveTarget} completed for ${habit.name}`}
              data-testid={`habit-count-${habit.id}`}
            />
          )}
        </div>
      </div>

      {contextMenu && (
        <ContextMenu
          position={contextMenu}
          onClose={() => setContextMenu(null)}
          items={[
            { label: "View stats", onClick: onViewStats, "data-testid": "context-stats" },
            {
              label: display.completion?.note ? "Edit note" : "Add note",
              onClick: onAddNote,
              "data-testid": "context-note",
            },
            { label: "Skip today", onClick: onSkip, "data-testid": "context-skip" },
            { label: "Backfill yesterday", onClick: onBackfill, "data-testid": "context-backfill" },
            { label: "Edit habit", onClick: onEdit, "data-testid": "context-edit" },
          ]}
        />
      )}
    </>
  );
}
